import React, { useState, useEffect } from "react"
import axios from "axios"
import { connect } from "react-redux"
import * as actions from "./../../store/actions"

import { Link, useLocation } from "react-router-dom"

import {
  Badge,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem
} from "@material-ui/core"

import {
  ExpandLess,
  ExpandMore,
  Person as PersonIcon,
  PersonOutline,
  Menu as MenuIcon,
  Close,
  Search,
  ShoppingBasketOutlined
} from "@material-ui/icons"

import AuthDialog from "./AuthDialog"
import ActionsDialog from "./ActionsDialog"
import SearchDialog from "./SearchDialog"

import "./Header.scss"

const LOGO = `${process.env.PUBLIC_URL}/logo_192_192.jpg`

const LanguageSwitch = ({ language, setLanguage, className }) => (
  <div
    className={`LanguageSwitch${className ? " " + className : ""}`}
    role="group"
    aria-label={language === "ua" ? "Мова сайту" : "Язык сайта"}
  >
    {["ua", "ru"].map(lang => (
      <button
        key={lang}
        type="button"
        className={`LanguageSwitch--option${language === lang ? " active" : ""}`}
        aria-pressed={language === lang}
        onClick={() => setLanguage(lang)}
      >
        {lang.toUpperCase()}
      </button>
    ))}
  </div>
)

const Header = props => {
  const {
    isLoggedIn,
    sessionUser,
    sessionBasket,
    sessionOrders,
    deleteFromBasket,
    onLogIn,
    onLogOut,
    translation,
    setLanguage
  } = props

  const [headerItems, setHeaderItems] = useState([])
  const [itemExpanded, setItemExpanded] = useState(-1)
  const [opened, setOpened] = useState(false)

  const [showAuthDialog, setShowAuthDialog] = useState(false)
  const [showActionsDialog, setShowActionsDialog] = useState(false)
  const [showSearchDialog, setShowSearchDialog] = useState(false)

  const [groupMenu, setGroupMenu] = useState({ anchor: null, index: -1 })

  const { pathname } = useLocation()
  const t = (ua, ru) => (translation.language === "ua" ? ua : ru)

  useEffect(() => {
    axios(`/nav/`, {
      withCredentials: true,
      crossdomain: true
    }).then(res => {
      setHeaderItems(res.data)
    })
  }, [])

  useEffect(() => {
    document.documentElement.lang = translation.language === "ru" ? "ru" : "uk"
  }, [translation.language])

  const logOut = () => {
    axios(`/logout/`, { withCredentials: true })
      .then(res => {
        onLogOut()
      })
      .catch(err => { })
  }

  const handleGroupClick = (linkIndex) => {
    linkIndex === itemExpanded
      ? setItemExpanded(-1)
      : setItemExpanded(linkIndex)
  }

  const closeGroupMenu = () => setGroupMenu({ anchor: null, index: -1 })

  const isGroupActive = item =>
    pathname.startsWith("/collections/" + item.category_group.url + "/")

  const categoryUrl = (item, category) =>
    "/collections/" + item.category_group.url + "/" + category.url

  const basketCount = sessionBasket.length

  return (
    <>
      <header className="Header">
        {showAuthDialog && (
          <AuthDialog
            onLogIn={onLogIn}
            translation={translation}
            onClose={() => setShowAuthDialog(false)}
          />
        )}
        {showActionsDialog && (
          <ActionsDialog
            sessionOrders={sessionOrders}
            translation={translation}
            onLogOut={logOut}
            onClose={() => setShowActionsDialog(false)}
            isLoggedIn={isLoggedIn}
            section={!props.isLoggedIn ? 1 : null}
            deleteFromBasket={deleteFromBasket}
            sessionBasket={sessionBasket}
            sessionUser={sessionUser}
          />
        )}
        {showSearchDialog && (
          <SearchDialog open={showSearchDialog} onClose={e => {
            e.stopPropagation()
            setShowSearchDialog(false)
          }} />
        )}

        <div className="Header--bar Container">
          <IconButton
            className="Header--burger"
            onClick={() => setOpened(!opened)}
            aria-label={t("Відкрити меню", "Открыть меню")}
            aria-expanded={opened}
            aria-controls="header-drawer"
          >
            <MenuIcon />
          </IconButton>

          <Link to="/" className="Header--brand" onClick={() => setOpened(false)}>
            <img src={LOGO} alt="" width="40" height="40" />
            <span className="Header--brand-name">Yva International</span>
            <span className="visually-hidden">{t(" — головна сторінка", " — главная страница")}</span>
          </Link>

          <button
            type="button"
            className="Header--search"
            onClick={() => setShowSearchDialog(true)}
          >
            <Search fontSize="small" />
            <span>{t("Пошук товарів", "Поиск товаров")}</span>
          </button>

          <div className="Header--actions">
            <IconButton
              className="Header--search-icon"
              onClick={() => setShowSearchDialog(true)}
              aria-label={t("Пошук товарів", "Поиск товаров")}
            >
              <Search />
            </IconButton>

            <LanguageSwitch
              className="Header--language"
              language={translation.language}
              setLanguage={setLanguage}
            />

            {!isLoggedIn ? (
              <>
                <Button
                  className="Header--login"
                  onClick={() => setShowAuthDialog(true)}
                  variant="text"
                  startIcon={<PersonOutline />}
                >
                  {t("Увійти", "Войти")}
                </Button>
                <IconButton
                  className="Header--login-icon"
                  onClick={() => setShowAuthDialog(true)}
                  aria-label={t("Увійти", "Войти")}
                >
                  <PersonOutline />
                </IconButton>
                <IconButton
                  className="Header--basket"
                  onClick={() => setShowActionsDialog(true)}
                  aria-label={t(`Кошик, товарів: ${basketCount}`, `Корзина, товаров: ${basketCount}`)}
                >
                  <Badge badgeContent={basketCount} color="primary" overlap="rectangular">
                    <ShoppingBasketOutlined />
                  </Badge>
                </IconButton>
              </>
            ) : (
              <IconButton
                className="Header--account"
                onClick={() => setShowActionsDialog(true)}
                aria-label={t("Мій акаунт", "Мой аккаунт")}
              >
                <PersonIcon />
              </IconButton>
            )}
          </div>
        </div>

        <nav className="Header--nav" aria-label={t("Каталог", "Каталог")}>
          <div className="Header--nav-inner Container">
            <Link
              to="/promotions"
              className={`Header--nav-link Header--nav-link--sale${pathname.startsWith("/promotions") ? " active" : ""}`}
              aria-current={pathname.startsWith("/promotions") ? "page" : undefined}
            >
              {t("Акційні товари", "Акционные товары")}
            </Link>
            <Link
              to="/feedbacks"
              className={`Header--nav-link${pathname.startsWith("/feedbacks") ? " active" : ""}`}
              aria-current={pathname.startsWith("/feedbacks") ? "page" : undefined}
            >
              {t("Відгуки", "Отзывы")}
            </Link>
            {headerItems.length > 0 && <span className="Header--nav-divider" aria-hidden="true" />}
            {headerItems.map((item, index) => (
              <button
                key={item.category_group.id}
                type="button"
                className={`Header--nav-link${isGroupActive(item) ? " active" : ""}${groupMenu.index === index ? " open" : ""}`}
                aria-haspopup="true"
                aria-expanded={groupMenu.index === index}
                onClick={e => setGroupMenu({ anchor: e.currentTarget, index })}
              >
                {translation.translateKey(item.category_group, "name")}
                <ExpandMore className="Header--nav-chevron" />
              </button>
            ))}
          </div>
        </nav>

        <Menu
          anchorEl={groupMenu.anchor}
          open={Boolean(groupMenu.anchor)}
          onClose={closeGroupMenu}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          classes={{ paper: "Header--menu" }}
        >
          {groupMenu.index >= 0 && headerItems[groupMenu.index] &&
            headerItems[groupMenu.index].categories.map(category => (
              <MenuItem
                key={category.id}
                component={Link}
                to={categoryUrl(headerItems[groupMenu.index], category)}
                onClick={closeGroupMenu}
                selected={pathname === categoryUrl(headerItems[groupMenu.index], category)}
              >
                {translation.translateKey(category, "name")}
              </MenuItem>
            ))}
        </Menu>
      </header>
      <div className="Header--spacer" aria-hidden="true" />

      <Drawer
        id="header-drawer"
        anchor="left"
        open={opened}
        onClose={() => setOpened(false)}
        classes={{ paper: "Header--drawer" }}
      >
        <div className="Header--drawer-head">
          <Link to="/" className="Header--brand" onClick={() => setOpened(false)}>
            <img src={LOGO} alt="" width="40" height="40" />
            <span className="Header--brand-name">Yva International</span>
          </Link>
          <IconButton onClick={() => setOpened(false)} aria-label={t("Закрити меню", "Закрыть меню")}>
            <Close />
          </IconButton>
        </div>

        <nav aria-label={t("Каталог", "Каталог")}>
          <List className="Header--drawer-list">
            <ListItem button component={Link} to="/" onClick={() => setOpened(false)} selected={pathname === "/"}>
              <ListItemText primary={t("Головна", "Главная")} />
            </ListItem>
            <ListItem button component={Link} to="/promotions" onClick={() => setOpened(false)} selected={pathname.startsWith("/promotions")}>
              <ListItemText primary={t("Акційні товари", "Акционные товары")} className="Header--drawer-sale" />
            </ListItem>
            <ListItem button component={Link} to="/feedbacks" onClick={() => setOpened(false)} selected={pathname.startsWith("/feedbacks")}>
              <ListItemText primary={t("Відгуки", "Отзывы")} />
            </ListItem>
          </List>
          <Divider />
          <List className="Header--drawer-list">
            {headerItems.map((item, index) => (
              <React.Fragment key={item.category_group.id}>
                <ListItem
                  button
                  onClick={() => handleGroupClick(index)}
                  aria-expanded={index === itemExpanded}
                >
                  <ListItemText primary={translation.translateKey(item.category_group, "name")} />
                  {index === itemExpanded ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                <Collapse in={index === itemExpanded} timeout="auto" unmountOnExit>
                  <List disablePadding className="Header--drawer-sublist">
                    {item.categories.map(category => (
                      <ListItem
                        key={category.id}
                        button
                        component={Link}
                        to={categoryUrl(item, category)}
                        onClick={() => setOpened(false)}
                        selected={pathname === categoryUrl(item, category)}
                      >
                        <ListItemText primary={translation.translateKey(category, "name")} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        </nav>

        <div className="Header--drawer-foot">
          <LanguageSwitch language={translation.language} setLanguage={setLanguage} />
          {!isLoggedIn && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PersonOutline />}
              onClick={() => {
                setOpened(false)
                setShowAuthDialog(true)
              }}
            >
              {t("Увійти", "Войти")}
            </Button>
          )}
        </div>
      </Drawer>
    </>
  )
}

const mapStateToProps = state => {
  return {
    isLoggedIn: state.sessionReducer.isLoggedIn,
    sessionUser: state.sessionReducer.user,
    sessionOrders: state.sessionReducer.orders,
    sessionBasket: state.sessionReducer.basket,
    translation: state.translationReducer
  }
}

const mapDispatchToProps = dispatch => {
  return {
    onLogIn: logInPayload =>
      dispatch({ type: actions.APPLY_SESSION, payload: logInPayload }),
    onLogOut: () => dispatch({ type: actions.LOG_OUT }),
    setLanguage: language =>
      dispatch({ type: actions.SET_LANGUAGE, payload: language }),
    deleteFromBasket: itemID =>
      dispatch({ type: actions.DELETE_FROM_BASKET, payload: itemID })
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Header)
